import type { DayPlanGroupRow } from '@trafficpulse/shared';

import type { DayPlanContainerRow, DropGenContainerSummary } from '../../drop-generator.types';
import { dropListSequence, type DropListLine, type TodayListEntry, todayListOrder } from './lists/today-list-order';

/**
 * A board row's group: today's lists in send order (19/10/2026 — the cell's
 * hover reads `todayOrder`) and every live drop's list in send order
 * (`sequence`, 26/10/2026 — the cell itself; `lists/container-day-lines.ts`
 * merges the row's groups and counts the lists no drop uses).
 */
export type BoardGroupRow = DayPlanGroupRow & { todayOrder: TodayListEntry[]; sequence: DropListLine[] };

/** Why a group's list gets no drop today (26/10/2026, user: "I want every list has the time"): a short tag and its explanation. */
export function noDropWhy(group: Pick<BoardGroupRow, 'listGroupId' | 'paused'>, slots: DropGenContainerSummary['slots']): { tag: string; why: string } {
  if (group.paused) return { tag: 'paused', why: 'Every list of this group is burned — Reset pool starts the order over.' };
  // The planner sends a slot only to the groups it names (slot-drops.ts) — a slot naming none sends nothing.
  const reaches = slots.some((slot) => slot.enabled && (slot.listGroupIds === undefined || slot.listGroupIds.includes(group.listGroupId)));
  if (!reaches)
    return { tag: 'no slot', why: 'No drop slot of this container sends to this group — add the group to a slot in General & Slots › Drop slots.' };
  return { tag: 'cut today', why: "Its drop was cut today — an hour rule, the daily cap or the send window; the Day timeline's warnings say which." };
}

/** Explicit slot pins may differ from the group's rotation cursor. Show the lists the drops actually use. */
export function boardListRows(preview: DayPlanContainerRow | undefined): BoardGroupRow[] {
  return (preview?.groups ?? []).map((group) => {
    const todayOrder = todayListOrder(preview, group);
    const sequence = dropListSequence(preview, group);
    if (!todayOrder.length) return { ...group, todayOrder, sequence };
    const today = todayOrder.map(({ wiserId, name }) => ({ wiserId, name }));
    return { ...group, todayOrder, sequence, listOfDay: today[0], listsToday: today.length > 1 ? today : undefined };
  });
}
